import { supabase } from '../integrations/client.ts';

/**
 * Classification result for a course topic
 */
export interface TopicClassification {
    requiresRealtime: boolean;
    isSkillBased: boolean; // True for skill-based courses (programming, trading, design), false for knowledge-based (history, news, economics)
    reason: string;
    confidence: 'high' | 'medium' | 'low';
}

/**
 * Cache for topic classifications to avoid repeated API calls
 * Key: normalized topic name, Value: classification result
 */
const classificationCache = new Map<string, TopicClassification>();

/**
 * Normalizes topic name for cache lookup (lowercase, trim, remove extra spaces)
 */
function normalizeTopicName(topic: string): string {
    return topic.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Invokes AWS Bedrock Nova Lite for topic classification
 * This is a lightweight, cheap model perfect for quick analysis
 */
async function invokeNovaLite(prompt: string): Promise<string> {
    try {
        const { data, error } = await supabase.functions.invoke('bedrock-invocations', {
            body: {
                prompt,
                model: 'nova-lite' // We'll need to add this model support to the edge function
            }
        });

        if (error) {
            
            throw new Error(`Failed to invoke Nova Lite: ${error.message}`);
        }

        if (!data?.content || !Array.isArray(data.content)) {
            throw new Error('Invalid response format from Nova Lite');
        }

        const textBlock = data.content.find((block: any) => block.type === 'text');
        return textBlock?.text || '';
    } catch (err) {
        
        throw err;
    }
}

/**
 * Analyzes a course topic to determine if it requires real-time information
 * Uses AWS Nova Lite for fast, cost-effective classification
 *
 * @param topic - The course subject name (e.g., "Python Programming", "Current Events 2024")
 * @returns Classification result indicating if real-time data is needed
 */
export async function classifyTopic(topic: string): Promise<TopicClassification> {
    const normalizedTopic = normalizeTopicName(topic);

    // Check cache first
    if (classificationCache.has(normalizedTopic)) {
        
        return classificationCache.get(normalizedTopic)!;
    }

    

    const classificationPrompt = `You are a topic classifier for an educational platform. Analyze the following course topic and determine TWO things:
1. If it requires REAL-TIME web search data or if STATIC KNOWLEDGE is sufficient
2. If it's a SKILL-BASED course (practical, hands-on) or KNOWLEDGE-BASED course (informational, theoretical)

Course Topic: "${topic}"

===== REAL-TIME vs STATIC =====

Topics that NEED REAL-TIME data include:
- Current events, news, or recent developments
- Technology versions, frameworks, or tools (e.g., "Python 3.13", "React 19", "Latest AI Models")
- Market data, stock prices, economic indicators, financial trends
- Political events, elections, current policies
- Sports scores, recent games, current players/teams
- Recent scientific discoveries or medical breakthroughs
- Time-sensitive statistics or demographics
- Any topic explicitly mentioning "2024", "2025", "latest", "current", "recent", "new"

Topics that DON'T NEED real-time data (STATIC KNOWLEDGE sufficient):
- Historical events, people, or eras
- Fundamental concepts in mathematics, physics, chemistry, biology
- Programming language fundamentals (syntax, core concepts, algorithms)
- Literature, philosophy, art history
- Grammar, languages, linguistics fundamentals
- Music theory, composition basics
- General psychology, sociology concepts
- Established scientific theories and principles
- Basic business, marketing, or management concepts

===== SKILL-BASED vs KNOWLEDGE-BASED =====

SKILL-BASED courses (practical, hands-on, require practice and application):
- Programming languages (Python, JavaScript, Java, C++, etc.)
- Web development, mobile app development, software engineering
- Data science, machine learning, AI development
- Trading, investing, financial analysis (with practical exercises)
- Graphic design, video editing, photo editing
- 3D modeling, animation, game development
- Music production, audio engineering, music composition
- Writing code, technical writing, copywriting
- CAD, engineering design, architecture
- Digital marketing (with practical campaigns)
- Cooking, baking (with recipes to follow)
- Foreign language learning (with practice exercises)
- **ALL STEM SUBJECTS** - Mathematics (calculus, algebra, geometry, statistics), Physics, Chemistry, Biology, Engineering
- Problem-solving based courses (requires working through problems and exercises)
- Any topic focused on building, creating, developing, or practicing with problems

KNOWLEDGE-BASED courses (informational, theoretical, focus on understanding):
- History (any period, region, or event)
- News, journalism, current events (reading about events, not creating content)
- Economics theory (not trading), macroeconomics, microeconomics
- Political science, philosophy, sociology
- Literature analysis, art history, music history
- Psychology, anthropology
- Geography, astronomy (observational only)
- Business theory, management theory
- Law, legal studies
- Any topic focused on learning ABOUT something rather than DOING something

Respond ONLY with one of these exact formats:

REALTIME|SKILL|HIGH|reason here
REALTIME|SKILL|MEDIUM|reason here
REALTIME|KNOWLEDGE|HIGH|reason here
REALTIME|KNOWLEDGE|MEDIUM|reason here
STATIC|SKILL|HIGH|reason here
STATIC|SKILL|MEDIUM|reason here
STATIC|KNOWLEDGE|HIGH|reason here
STATIC|KNOWLEDGE|MEDIUM|reason here

Example responses:
- "Python Programming" → STATIC|SKILL|HIGH|Programming is a practical skill requiring hands-on coding practice
- "World War II History" → STATIC|KNOWLEDGE|HIGH|Historical events are informational and don't involve hands-on practice
- "Stock Market Trading" → REALTIME|SKILL|HIGH|Trading involves practical application and current market data
- "Economics 101" → STATIC|KNOWLEDGE|HIGH|Economic theory is informational without practical trading exercises
- "Web Development with React" → STATIC|SKILL|HIGH|Building websites is a practical, hands-on skill
- "News and Current Events" → REALTIME|KNOWLEDGE|HIGH|Reading about news is informational, not a practical skill
- "Graphic Design in Photoshop" → STATIC|SKILL|HIGH|Design involves creating and practicing with tools
- "Philosophy and Ethics" → STATIC|KNOWLEDGE|HIGH|Philosophical concepts are theoretical and informational

Classify: "${topic}"`;

    try {
        const response = await invokeNovaLite(classificationPrompt);

        // Parse the response (new format: REALTIME|SKILL|HIGH|reason)
        const match = response.trim().match(/^(REALTIME|STATIC)\|(SKILL|KNOWLEDGE)\|(HIGH|MEDIUM|LOW)\|(.+)$/i);

        if (!match) {
            
            // Default to static knowledge-based (safer defaults)
            const defaultClassification: TopicClassification = {
                requiresRealtime: false,
                isSkillBased: false,
                reason: 'Classification parsing failed, defaulting to static knowledge',
                confidence: 'low'
            };
            classificationCache.set(normalizedTopic, defaultClassification);
            return defaultClassification;
        }

        const [, type, courseType, confidence, reason] = match;
        const classification: TopicClassification = {
            requiresRealtime: type.toUpperCase() === 'REALTIME',
            isSkillBased: courseType.toUpperCase() === 'SKILL',
            reason: reason.trim(),
            confidence: confidence.toLowerCase() as 'high' | 'medium' | 'low'
        };

        

        // Cache the result
        classificationCache.set(normalizedTopic, classification);

        return classification;
    } catch (error) {
        

        // Fallback: use heuristic-based classification
        return fallbackClassification(topic);
    }
}

/**
 * Fallback heuristic-based classification when API fails
 * Uses simple keyword matching
 */
function fallbackClassification(topic: string): TopicClassification {
    const lowerTopic = topic.toLowerCase();

    // Keywords that strongly suggest real-time data is needed
    const realtimeKeywords = [
        'news', 'current', 'latest', 'recent', 'today', '2024', '2025',
        'breaking', 'trending', 'modern', 'contemporary', 'now',
        'market', 'stock', 'trading', 'economy', 'economics',
        'politics', 'election', 'policy', 'government',
        'version', 'update', 'new release', 'latest version'
    ];

    // Keywords that suggest static knowledge is sufficient
    const staticKeywords = [
        'history', 'historical', 'fundamentals', 'basics', 'introduction',
        'theory', 'principles', 'concepts', 'mathematics', 'calculus',
        'algebra', 'geometry', 'physics', 'chemistry', 'biology',
        'literature', 'philosophy', 'art', 'music', 'grammar'
    ];

    // Keywords that suggest skill-based (practical, hands-on)
    const skillKeywords = [
        'programming', 'coding', 'development', 'building', 'creating',
        'design', 'trading', 'investing', 'python', 'javascript', 'java',
        'react', 'web', 'app', 'software', 'data science', 'machine learning',
        'graphic', 'video editing', '3d', 'animation', 'game', 'music production',
        'cooking', 'baking', 'language learning', 'speaking', 'writing code',
        'photoshop', 'illustrator', 'cad', 'marketing campaign', 'seo'
    ];

    // Keywords that suggest knowledge-based (informational, theoretical)
    const knowledgeKeywords = [
        'history', 'news', 'journalism', 'economics theory', 'political science',
        'philosophy', 'sociology', 'literature', 'art history', 'music history',
        'biology', 'chemistry', 'physics', 'psychology', 'anthropology',
        'geography', 'astronomy', 'business theory', 'management', 'law',
        'events', 'understanding', 'analysis', 'study of', 'introduction to'
    ];

    const hasRealtimeKeyword = realtimeKeywords.some(keyword => lowerTopic.includes(keyword));
    const hasStaticKeyword = staticKeywords.some(keyword => lowerTopic.includes(keyword));
    const hasSkillKeyword = skillKeywords.some(keyword => lowerTopic.includes(keyword));
    const hasKnowledgeKeyword = knowledgeKeywords.some(keyword => lowerTopic.includes(keyword));

    // Determine realtime vs static
    const requiresRealtime = hasRealtimeKeyword && !hasStaticKeyword;

    // Determine skill vs knowledge (default to knowledge if unclear)
    const isSkillBased = hasSkillKeyword && !hasKnowledgeKeyword;

    const classification: TopicClassification = {
        requiresRealtime,
        isSkillBased,
        reason: `Heuristic: ${requiresRealtime ? 'Realtime' : 'Static'} + ${isSkillBased ? 'Skill-based' : 'Knowledge-based'} based on keyword matching`,
        confidence: (hasSkillKeyword || hasKnowledgeKeyword) ? 'medium' : 'low'
    };

    classificationCache.set(normalizeTopicName(topic), classification);
    return classification;
}

/**
 * Clears the classification cache (useful for testing or memory management)
 */
export function clearClassificationCache(): void {
    classificationCache.clear();
    
}

/**
 * Gets the current cache size (for monitoring)
 */
export function getClassificationCacheSize(): number {
    return classificationCache.size;
}
